import { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from '../components/ui/Sidebar';
import MobileBottomNav from '../components/ui/MobileBottomNav';
import SalonSelector from '../components/ui/SalonSelector';
import { Bell, Search, ChevronDown, Upload, User, Settings, LogOut, HelpCircle, BookOpen, DollarSign, Package, Shield } from 'lucide-react';
import { NotificationBanner } from '../components/NotificationBanner';
import ImportDataModal from '../components/ui/ImportDataModal';
import type { AvailableSalon, AuthResponse } from '../api/authApi';
import { salonApi } from '../api/salonApi';
import { useTutorial } from '../hooks/useTutorial';
import { bookingApi, type BookingAlertsResponse } from '../api/bookingApi';
import { useSubscription } from '../hooks/useSubscription';


interface UserInfo {
    fullName: string;
    email: string; // Added email for resend
    role: string;
    pictureUrl?: string;
    salonId: string | null;
    availableSalons: AvailableSalon[];
    emailVerified: boolean; // Added verify flag
}

const MainLayout = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<UserInfo | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    // Verification State
    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    const { isOwner, isManager, isBetaMode } = useSubscription();

    // Notifications State - RESTORED
    const [alerts, setAlerts] = useState<BookingAlertsResponse | null>(null);
    const [showNotifications, setShowNotifications] = useState(false);

    useEffect(() => {
        if ((isOwner || isManager) && user?.salonId) {
            const fetchAlerts = () => {
                bookingApi.getAlerts(user.salonId!).then(setAlerts).catch(console.error);
            };
            fetchAlerts();
            const interval = setInterval(fetchAlerts, 60000);
            return () => clearInterval(interval);
        }
    }, [isOwner, isManager, user?.salonId]);

    // Branding & UI State - RESTORED
    const [salonLogoUrl, setSalonLogoUrl] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { startTutorial } = useTutorial();

    // Load User
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData: AuthResponse = JSON.parse(userStr);
            setUser({
                fullName: userData.fullName || 'Usuario',
                email: userData.email,
                role: userData.role || 'User',
                pictureUrl: userData.pictureUrl,
                salonId: userData.salonId,
                availableSalons: userData.availableSalons || [],
                emailVerified: userData.emailVerified
            });
        }
    }, []);

    // Load Branding
    useEffect(() => {
        const loadBranding = async () => {
            if (!user?.salonId) return;

            try {
                const salon = await salonApi.getSalonById(user.salonId);
                const root = document.documentElement;

                if (salon.primaryColor) {
                    root.style.setProperty('--color-primary', salon.primaryColor);
                    const secondary = salon.secondaryColor || salon.primaryColor + '40';
                    root.style.setProperty('--color-secondary', secondary);
                    root.style.setProperty('--color-primary-light', secondary);
                }

                if (salon.secondaryColor) {
                    root.style.setProperty('--color-secondary', salon.secondaryColor);
                    root.style.setProperty('--color-primary-light', salon.secondaryColor);
                }

                if (salon.logoUrl) {
                    localStorage.setItem('salonLogoUrl', salon.logoUrl);
                    setSalonLogoUrl(salon.logoUrl);
                }
            } catch (error) {
                console.error('Failed to load salon branding:', error);
            }
        };

        loadBranding();
    }, [user?.salonId]);

    // Close Dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const handleResendVerification = async () => {
        if (!user?.email || isResending) return;

        try {
            setIsResending(true);
            await import('../api/authApi').then(m => m.authApi.resendVerificationEmail(user.email));
            setResendSuccess(true);
            setTimeout(() => setResendSuccess(false), 5000);
        } catch (error) {
            console.error('Failed to resend verification:', error);
            // Optionally show toast error
        } finally {
            setIsResending(false);
        }
    };

    const handleExitImpersonation = () => {
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
            localStorage.setItem('token', adminToken);
            localStorage.removeItem('adminToken');
            // We also need to reload or fetch the original admin user info
            // For simplicity, redirect to admin dashboard
            window.location.href = '/admin';
        }
    };

    // ... (rest of methods)

    return (
        <div className="min-h-screen bg-gray-50/50 font-sans text-gray-900">
            {/* Desktop Sidebar */}
            <Sidebar />

            {/* Main Content Area */}
            <main className="md:ml-64 min-h-screen flex flex-col pb-20 md:pb-0 transition-all duration-300">

                {/* IMPERSONATION BANNER */}
                {localStorage.getItem('adminToken') && (
                    <div className="bg-gradient-to-r from-purple-700 to-indigo-800 px-4 py-3 flex items-center justify-between text-white text-xs font-bold sticky top-0 z-[70] shadow-lg">
                        <div className="flex items-center gap-3">
                            <Shield className="w-4 h-4 text-purple-200" />
                            <p className="tracking-wide">
                                <span className="opacity-70 font-normal">SOPORTE TÉCNICO ACTIVO:</span> Suplantando a <b>{user?.fullName}</b> ({user?.email})
                            </p>
                        </div>
                        <button 
                            onClick={handleExitImpersonation}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/20 transition-all flex items-center gap-2"
                        >
                            <LogOut size={14} />
                            Salir de Soporte
                        </button>
                    </div>
                )}

                {/* BETA MODE BANNER - Fixed Premium Style */}
                {isBetaMode && (
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2 flex items-center justify-center text-white text-xs font-bold sticky top-0 z-[60] shadow-md">
                        <div className="flex items-center gap-2">
                            <span className="flex h-2 w-2 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                            </span>
                            <p className="uppercase tracking-widest">
                                MODO BETA ACTIVO • FUNCIONES PREMIUM ILIMITADAS • TOTALMENTE GRATIS
                            </p>
                        </div>
                    </div>
                )}

                {/* Verification Banner */}
                {user && !user.emailVerified && (
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between text-amber-800 text-sm sticky top-0 z-50">
                        <div className="flex items-center gap-3">
                            <span className="bg-amber-200 text-amber-800 p-1 rounded-full">⚠️</span>
                            <p>
                                <strong>Verifica tu cuenta:</strong> Revisa tu correo electrónico para activar todas las funciones.
                            </p>
                        </div>
                        <button
                            onClick={handleResendVerification}
                            disabled={isResending || resendSuccess}
                            className="text-amber-700 underline hover:text-amber-900 font-medium disabled:opacity-50 disabled:no-underline"
                        >
                            {resendSuccess ? '¡Enviado!' : isResending ? 'Enviando...' : 'Reenviar Email'}
                        </button>
                    </div>
                )}

                {/* Premium Glass Topbar - Sticky */}
                <header className={`h-18 px-6 md:px-8 flex items-center justify-between sticky ${isBetaMode ? 'top-8' : (user && !user.emailVerified ? 'top-12' : 'top-0')} z-40 backdrop-blur-xl bg-white/80 border-b border-white/40 shadow-sm transition-all duration-300`}>


                    {/* Left side - Search */}
                    <div className="flex items-center gap-4 flex-1 max-w-xl">
                        {/* Mobile Logo Only */}
                        <div className="md:hidden flex items-center gap-3 mr-2">
                            {salonLogoUrl ? (
                                <img src={salonLogoUrl} alt="Logo" className="h-8 max-w-[100px] object-contain" />
                            ) : (
                                <h1 className="text-xl font-bold bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] bg-clip-text text-transparent">Cresta</h1>
                            )}
                        </div>

                        {/* Search Bar (Hidden on small mobile) */}
                        <div className="hidden md:flex items-center relative w-full max-w-sm group">
                            <Search className="w-5 h-5 text-gray-400 absolute left-3 group-focus-within:text-[var(--color-primary)] transition-colors" />
                            <input
                                type="text"
                                placeholder="Buscar cliente, cita o servicio..."
                                className="pl-10 pr-4 py-2.5 bg-gray-100/50 border-none rounded-xl text-sm w-full focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:bg-white transition-all outline-none placeholder-gray-400"
                            />
                        </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex items-center gap-3 md:gap-5">
                        {/* Salon Selector */}
                        {user && user.availableSalons.length > 1 && (
                            <SalonSelector
                                currentSalonId={user.salonId}
                                availableSalons={user.availableSalons}
                                onSalonChange={(newSalonId) => setUser(prev => prev ? { ...prev, salonId: newSalonId } : null)}
                            />
                        )}

                        {/* Notifications */}
                        {(isOwner || isManager) && (
                            <div className="relative">
                                <button
                                    className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-500 hover:text-[var(--color-primary)] transition-all"
                                    onClick={() => setShowNotifications(!showNotifications)}
                                >
                                    <Bell className="w-5 h-5" />
                                    {alerts && (alerts.overdueCount + alerts.upcomingCount + alerts.inProgressCount > 0) && (
                                        <span className={`absolute top-2 right-2 w-2.5 h-2.5 rounded-full border-2 border-white ${alerts.overdueCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-[var(--color-primary)]'}`}></span>
                                    )}
                                </button>

                                {showNotifications && (
                                    <div className="absolute right-0 mt-3 w-80 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-black/5">
                                        <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                                            <h3 className="font-semibold text-gray-900 text-sm">Notificaciones</h3>
                                            {alerts?.overdueCount! > 0 && (
                                                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100 shadow-sm">
                                                    {alerts?.overdueCount} Pendientes
                                                </span>
                                            )}
                                        </div>

                                        <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                                            {!alerts?.alerts.length ? (
                                                <div className="p-8 text-center text-gray-400 text-sm flex flex-col items-center">
                                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                                        <Bell className="w-6 h-6 opacity-30" />
                                                    </div>
                                                    <p className="font-medium text-gray-500">Estás al día</p>
                                                    <p className="text-xs mt-1 text-gray-400">No hay alertas pendientes por ahora.</p>
                                                </div>
                                            ) : (
                                                alerts.alerts.map(alert => (
                                                    <button
                                                        key={alert.appointmentId}
                                                        className="w-full px-4 py-3 text-left hover:bg-gray-50/80 border-b border-gray-50 last:border-0 transition-colors group relative"
                                                        onClick={() => { navigate('/app/agenda'); setShowNotifications(false); }}
                                                    >
                                                        <div className="flex gap-3 items-start">
                                                            <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 shadow-sm ${alert.type.includes('OVERDUE') ? 'bg-red-500 ring-2 ring-red-100' : 'bg-amber-500'}`} />
                                                            <div>
                                                                <p className={`text-sm font-medium leading-snug ${alert.type.includes('OVERDUE') ? 'text-red-700' : 'text-gray-900'}`}>
                                                                    {alert.message}
                                                                </p>
                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                                                        {new Date(alert.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 font-medium">
                                                                        {alert.employeeName || 'Estilista'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User Profile */}
                        <div className="relative pl-2 border-l border-gray-200" ref={dropdownRef}>
                            <button
                                className="flex items-center gap-3 hover:bg-gray-100/50 rounded-xl p-1.5 pr-3 transition-colors cursor-pointer group"
                                onClick={() => setShowDropdown(!showDropdown)}
                            >
                                <div className="relative">
                                    {user?.pictureUrl ? (
                                        <img src={user.pictureUrl} alt={user.fullName} className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm group-hover:ring-[var(--color-primary)]/30 transition-all" />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-teal-400 flex items-center justify-center text-white font-bold text-sm shadow-md">
                                            {user ? getInitials(user.fullName) : 'U'}
                                        </div>
                                    )}
                                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                                </div>

                                <div className="hidden lg:block text-left">
                                    <p className="text-sm font-semibold text-gray-900 leading-none mb-1">{user?.fullName || 'Usuario'}</p>
                                    <p className="text-xs text-gray-500 leading-none">{user?.role || 'User'}</p>
                                </div>
                                <ChevronDown className={`hidden md:block w-4 h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Profile Dropdown */}
                            {showDropdown && (
                                <div className="absolute right-0 mt-3 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-black/5">
                                    <div className="px-5 py-3 border-b border-gray-100/50 mb-1">
                                        <p className="text-sm font-bold text-gray-900">{user?.fullName}</p>
                                        <p className="text-xs text-gray-500">{user?.role}</p>
                                    </div>

                                    <div className="px-2 space-y-0.5">
                                        <DropdownItem icon={User} label="Mi Perfil" onClick={() => { setShowDropdown(false); navigate('/app/settings?tab=account'); }} />
                                        {user?.role === 'SUPER_ADMIN' && (
                                            <DropdownItem icon={Shield} label="Cresta Master" onClick={() => { setShowDropdown(false); navigate('/admin'); }} color="text-teal-600" />
                                        )}
                                        <DropdownItem icon={Upload} label="Importar Datos" onClick={() => { setShowDropdown(false); setShowImportModal(true); }} color="text-emerald-600" />
                                        <DropdownItem icon={DollarSign} label="Finanzas" onClick={() => { setShowDropdown(false); navigate('/app/finances'); }} color="text-green-600" />
                                        <DropdownItem icon={Package} label="Inventario" onClick={() => { setShowDropdown(false); navigate('/app/inventory'); }} color="text-orange-600" />
                                        <DropdownItem icon={Settings} label="Configuración" onClick={() => { setShowDropdown(false); navigate('/app/settings?tab=branding'); }} />
                                    </div>

                                    <div className="my-2 border-t border-gray-100/50"></div>

                                    <div className="px-2 space-y-0.5">
                                        <DropdownItem icon={HelpCircle} label="Ver Tutorial" onClick={() => { setShowDropdown(false); startTutorial(true); }} color="text-blue-600" />
                                        <DropdownItem icon={BookOpen} label="Centro de Ayuda" onClick={() => { setShowDropdown(false); navigate('/help'); }} color="text-purple-600" />
                                    </div>

                                    <div className="my-2 border-t border-gray-100/50"></div>

                                    <div className="px-2">
                                        <button
                                            className="w-full px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-3 transition-colors font-medium"
                                            onClick={handleLogout}
                                        >
                                            <LogOut className="w-4 h-4" />
                                            Cerrar Sesión
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content */}
                <div className="p-4 md:p-8 flex-1 overflow-y-auto">
                    <Outlet />
                </div>
            </main>

            <MobileBottomNav />

            {showImportModal && (
                <ImportDataModal
                    onClose={() => setShowImportModal(false)}
                    onImportSuccess={() => window.location.reload()}
                />
            )}

            <NotificationBanner />
        </div>
    );
};

// Helper Component for consistency
const DropdownItem = ({ icon: Icon, label, onClick, color = "text-gray-500" }: { icon: any, label: string, onClick: () => void, color?: string }) => (
    <button
        className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100/80 rounded-xl flex items-center gap-3 transition-all group"
        onClick={onClick}
    >
        <Icon className={`w-4 h-4 ${color} group-hover:scale-110 transition-transform`} />
        <span className="font-medium text-gray-700">{label}</span>
    </button>
);

export default MainLayout;
